import {Inject, Injectable} from '@angular/core';
import {intComparator} from '@application/helper/int-comparator';
import {DurationFormatPipe} from '@application/pipes/duration-format.pipe';
import {MachineStatus} from '@domain/machine/machine-status.enum';
import {MachineType} from '@domain/machine/machine-type.enum';
import {ProductionOrderProgress} from '@domain/machine/production-order-progress';
import {StandardSpeed} from '@domain/machine/standard-speed.enum';
import {Permission} from '@domain/profile/permission.enum';
import {ReportItemTemplateField} from '@domain/report-data-set/report-item-template-field.enum';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {AssertionUtils, ColDefBuilderFactoryService, GridOptionsBuilder, NoDataOverlayComponentParams, OverlayComponentParams, StringUtils, TranslateService} from '@vdw/angular-component-library';
import {ColDef, ICellRendererParams, ITooltipParams, ValueFormatterParams, ValueGetterParams} from 'ag-grid-community';
import {BaseOverviewMachineGroup} from '../../base-overview-machine-group';
import {OverviewMachine} from '../../overview-machine';
import {ReportItem} from '../../report-item';
import {OverviewMachineEfficiencyCellRendererParams} from './overview-machine-efficiency/overview-machine-efficiency-cell-renderer-params.interface';
import {OverviewMachineEfficiencyComponent} from './overview-machine-efficiency/overview-machine-efficiency.component';
import {OverviewMachineTypeCellRendererParams} from './overview-machine-type/overview-machine-type-cell-renderer-params.interface';
import {OverviewMachineTypeComponent} from './overview-machine-type/overview-machine-type.component';

@Injectable({providedIn: 'root'})
export class MachineOverviewService {
  public constructor(
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication
  ) {}

  public getColumnDefs(isKioskMode: boolean): ColDef[] {
    const hasTexstateViewLiveOrderMessages = this.authentication.hasPermission(Permission.TEXSTATE_VIEW_LIVE_ORDER_MESSAGES);
    const hasTexstateViewLiveStatus = this.authentication.hasPermission(Permission.TEXSTATE_VIEW_LIVE_STATUS);
    const hasTexstateViewLiveDeclarationMessages = this.authentication.hasPermission(Permission.TEXSTATE_VIEW_LIVE_DECLARATION_MESSAGES);

    const efficiencyValueColDefBuilder = this.colDefBuilderFactoryService.getBuilder().withMinWidth(200).withComparator(intComparator).withCellClass('center');
    if (this.authentication.hasPermission(Permission.MACHINE_OVERVIEW_SHOW_EFFICIENTY_CURRENT_SHIFT)) {
      efficiencyValueColDefBuilder
        .withCellRenderer(OverviewMachineEfficiencyComponent, (params: ICellRendererParams) => {
          return {
            machine: params.data,
            useReportItemData: true
          } as OverviewMachineEfficiencyCellRendererParams;
        })
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.EFFICIENCY_CURRENT_SHIFT');
    } else {
      efficiencyValueColDefBuilder
        .withField('efficiency.value')
        .withCellRenderer(OverviewMachineEfficiencyComponent, (params: ICellRendererParams) => {
          return {
            machine: params.data,
            useReportItemData: false
          } as OverviewMachineEfficiencyCellRendererParams;
        })
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.EFFICIENCY_LAST_HOUR');
    }

    const colDefs: ColDef[] = [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('machineType')
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.TYPE')
        .withCellRenderer(OverviewMachineTypeComponent, {
          hasTexstateViewLiveStatusPermission: hasTexstateViewLiveStatus,
          hasTexstateViewLiveDeclarationMessagesPermission: hasTexstateViewLiveDeclarationMessages
        } as OverviewMachineTypeCellRendererParams)
        .withComparator(intComparator)
        .withFilterValueGetter((params: ValueGetterParams<OverviewMachine>) => this.translate.instant(`MACHINE.TYPES.${params.data.machineType}`))
        .withTooltipValueGetter((params: ITooltipParams) => this.translate.instant(`MACHINE.TYPES.${MachineType[params.value]}`))
        .withCellClass('cell-no-x-padding center')
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withValueGetter((params: ValueGetterParams) => this.machineNameValueGetter(params))
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('status')
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.STATUS')
        .withComparator((currentStatus: MachineStatus, nextStatus: MachineStatus) =>
          StringUtils.stringComparator(this.createStringFromStatusValue(currentStatus), this.createStringFromStatusValue(nextStatus))
        )
        .withValueFormatter((params: ValueFormatterParams) => this.createStatusValue(params))
        .withTooltipValueGetter((params: ITooltipParams) => this.createStatusValue(params))
        .withFilterValueGetter((params: ValueGetterParams<OverviewMachine>) => this.createStatusValue(params))
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('productionOrder.progress')
        .withHeaderName('MACHINE.PRODUCTION_TIME')
        .withComparator((currentProductionOrderProgress: ProductionOrderProgress, nextProductionOrderProgress: ProductionOrderProgress) =>
          this.compareProductionOrderProgresses(currentProductionOrderProgress, nextProductionOrderProgress)
        )
        .withTooltipValueGetter((params: ITooltipParams) => this.createProductionOrderProgressValue(params))
        .withValueFormatter((params: ValueFormatterParams) => this.createProductionOrderProgressValue(params))
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('endTime').withHeaderName('GENERAL.DATETIME.END_TIME').withDate().withDateMultiFilter().withInitialHide().build(),
      efficiencyValueColDefBuilder.build(),
      this.colDefBuilderFactoryService.getBuilder().withField('speed', true).withHeaderName('MACHINE.SPEED', 1, false, 'ppm').withNumericMultiFilter().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('machineGroups')
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.GROUPS')
        .withValueGetter((params: ValueGetterParams) => this.createMachineGroupsValue(params.data), true)
        .withComparator(StringUtils.stringComparator)
        .build()
    ];

    if (!isKioskMode) {
      colDefs.splice(
        5,
        0,
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('activeQuality.technicalName', true)
          .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.MACHINE_QUALITY')
          .withComparator(StringUtils.stringComparator)
          .build(),
        this.colDefBuilderFactoryService.getBuilder().withField('activeCreel.name', true).withHeaderName('TEXTILE_DATA.CREEL.CREEL').withComparator(StringUtils.stringComparator).build()
      );

      colDefs.splice(
        9,
        0,
        this.colDefBuilderFactoryService.getBuilder().withField('orderId', true).withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.ORDER_ID').withComparator(StringUtils.stringComparator).build()
      );
    }

    if (hasTexstateViewLiveOrderMessages) {
      colDefs.splice(
        3,
        0,
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('productionOrder.name')
          .withHeaderName('PRODUCTION_ORDER.PRODUCTION_ORDER')
          .withComparator(StringUtils.stringComparator)
          .withValueGetter((params: ValueGetterParams) => {
            const reportItem = params.data.reportItems.find((item: ReportItem) => item.field === ReportItemTemplateField.ORDER_NAME);
            return reportItem ? reportItem.value : null;
          })
          .withLockVisible()
          .build()
      );
    }

    return colDefs;
  }

  public getColumnDefsForSpeed(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withField('name', true).withHeaderName('GENERAL.NAME').build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('machineType')
        .withHeaderName('SUPPORT.MACHINES_LIST.TABLE_HEADER.TYPE')
        .withValueGetter((params: ValueGetterParams) => this.translate.instant(`MACHINE.TYPES.${MachineType[params.data.machineType]}`), true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardSpeed')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.STANDARD_SPEED')
        .withCellClass('right')
        .withCellRenderer((params: ICellRendererParams) =>
          params.value ? `${params.value} ${this.translate.instant(`MACHINE.DETAILS.STANDARD_SPEEDS.${params.data.plasticsMachineType?.standardSpeed ?? StandardSpeed.SPEED}`)}` : ''
        )
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardEfficiency')
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.STANDARD_EFFICIENCY')
        .withCellClass('right')
        .withCellRenderer((params: ICellRendererParams) => (params.value ? `${params.value} %` : ''))
        .build()
    ];
  }

  public getDefaultGridOptionsBuilder(builder: GridOptionsBuilder): GridOptionsBuilder {
    return builder
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: 'MACHINE.MACHINE',
        hideDescription: true
      } as NoDataOverlayComponentParams);
  }

  private machineNameValueGetter(params: ValueGetterParams): string {
    const overviewMachine: OverviewMachine = params.data;
    return !AssertionUtils.isEmpty(overviewMachine.name) ? overviewMachine.name : this.translate.instant('GENERAL.NO_DATA', {object: this.translate.instant('GENERAL.NAME').toLowerCase()});
  }

  private getProductionOrderProgressDuration(progress: ProductionOrderProgress): number {
    return AssertionUtils.isNullOrUndefined(progress) ? 0 : progress.timeSpentInSeconds;
  }

  private createStatusValue(params: ValueFormatterParams | ITooltipParams | ValueGetterParams): string {
    const machineOverview: OverviewMachine = params.data;

    if (AssertionUtils.isNullOrUndefined(machineOverview) || AssertionUtils.isNullOrUndefined(machineOverview.status)) {
      return '';
    }
    return this.createStringFromStatusValue(machineOverview.status);
  }

  private createStringFromStatusValue(status: MachineStatus): string {
    if (AssertionUtils.isNullOrUndefined(status)) {
      return '';
    }
    if (!this.authentication.hasPermission(Permission.TEXSTATE_VIEW_LIVE_STATUS)) {
      status = status === MachineStatus.DISCONNECTED ? status : MachineStatus.CONNECTED;
    }
    return this.translate.instant(`MACHINE.${MachineStatus[status]}`);
  }

  private createMachineGroupsValue(machineOverview: OverviewMachine): string {
    return machineOverview.machineGroups.length > 0
      ? machineOverview.machineGroups
          .map((machineGroup: BaseOverviewMachineGroup) => machineGroup.name)
          .sort(StringUtils.stringComparator)
          .join(', ')
      : null;
  }

  private createProductionOrderProgressValue(params: ValueFormatterParams | ITooltipParams): string {
    const machineOverview: OverviewMachine = params.data;
    if (
      AssertionUtils.isNullOrUndefined(machineOverview) ||
      AssertionUtils.isNullOrUndefined(machineOverview.productionOrder) ||
      AssertionUtils.isNullOrUndefined(machineOverview.productionOrder.progress)
    ) {
      return null;
    }
    return this.createStringFromProductionOrderProgress(machineOverview.productionOrder.progress);
  }

  private createStringFromProductionOrderProgress(progress: ProductionOrderProgress): string {
    if (AssertionUtils.isNullOrUndefined(progress)) {
      return null;
    }
    return new DurationFormatPipe().transform(this.getProductionOrderProgressDuration(progress), 's', 'd[d] h[h] m[m]');
  }

  private compareProductionOrderProgresses(valueA: ProductionOrderProgress, valueB: ProductionOrderProgress): number {
    const durationA: number = this.getProductionOrderProgressDuration(valueA);
    const durationB: number = this.getProductionOrderProgressDuration(valueB);

    let result = 0;
    if (durationA < durationB) {
      result = -1;
    } else if (durationA > durationB) {
      result = 1;
    }
    return result;
  }
}
